/**
 * usePayouts Hook
 *
 * Manages payout requests and history via Firestore real-time snapshots
 * and the requestPayout Cloud Function.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../services/firebase';
import { useAuth } from '../../../../contexts';

export interface PayoutRequest {
  id: string;
  amount: number;
  transferFee: number;
  netAmount: number;
  payoutMethodId: string;
  payoutMethodType: 'whish_money' | 'wire_transfer';
  payoutMethodName: string;
  status: 'requested' | 'processing' | 'completed' | 'failed';
  requestedAt: Date;
  processedAt?: Date;
  completedAt?: Date;
  transactionReference?: string;
  adminNotes?: string;
}

interface RequestPayoutData {
  amount: number;
  payoutMethodId: string;
}

const toDate = (val: unknown): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val === 'object' && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate();
  }
  const d = new Date(val as string | number);
  return isNaN(d.getTime()) ? new Date() : d;
};

export const usePayouts = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setPayouts([]);
      setIsLoading(false);
      return;
    }

    const payoutsRef = collection(db, 'payouts');
    const q = query(
      payoutsRef,
      where('hostId', '==', user.uid),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: PayoutRequest[] = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            amount: d.amount || 0,
            transferFee: d.transferFee || 0,
            netAmount: d.netAmount || 0,
            payoutMethodId: d.payoutMethodId || '',
            payoutMethodType: d.payoutMethodType || 'whish_money',
            payoutMethodName: d.payoutMethodName || '',
            status: d.status || 'requested',
            requestedAt: toDate(d.requestedAt),
            processedAt: d.processedAt ? toDate(d.processedAt) : undefined,
            completedAt: d.completedAt ? toDate(d.completedAt) : undefined,
            transactionReference: d.transactionReference || undefined,
            adminNotes: d.adminNotes || undefined,
          };
        });
        setPayouts(items);
        setIsLoading(false);
      },
      (err) => {
        console.error('Payouts snapshot error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const requestPayout = useCallback(
    async (data: RequestPayoutData): Promise<boolean> => {
      setIsRequesting(true);
      setError(null);

      try {
        const callable = httpsCallable<
          { payoutMethodId: string },
          { payoutId: string; amount: number }
        >(functions, 'requestPayout');

        await callable({ payoutMethodId: data.payoutMethodId });
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to request payout';
        setError(message);
        return false;
      } finally {
        setIsRequesting(false);
      }
    },
    []
  );

  return {
    payouts,
    isLoading,
    isRequesting,
    error,
    requestPayout,
  };
};

export default usePayouts;
